// Tracks "chats currently arriving via Welcome" so the UI can surface a
// transient "Receiving N chats…" banner. Populated by
// `useIncomingConversations` whenever it persists a NEW conversation
// row; drained automatically `RECENT_JOIN_WINDOW_MS` after the last
// join, so the banner is self-clearing and doesn't need an explicit
// "done" signal from the linking flow.
//
// In-memory only (no persistence). Surviving a reload is undesirable —
// after a fresh load the user already sees the current chat list, no
// "receiving" state to restore.

import { create } from 'zustand';

/** How long after the last Welcome we keep the banner visible. */
export const RECENT_JOIN_WINDOW_MS = 5_000;

/**
 * Linking history-sync progress for the "Syncing conversations… N%" banner on
 * the RECEIVER (newly-linked) device. Driven by `consumePendingLinkingHistory`
 * as it drains + replays the sealed history bundles the existing device shipped.
 *
 *  - `active`: the drain is in flight (a stash is present). Banner is visible.
 *  - `total`:  the batch denominator — `batch_total` from the first transfer
 *              that carried it. `0` until the first batch arrives, or when the
 *              sender is older and never sent it (→ indeterminate fallback).
 *  - `done`:   bundles replayed so far (the numerator).
 *
 * Percent = round(done / total * 100). When `total` is 0 while active, the
 * banner shows an indeterminate "Syncing conversations… M messages" instead.
 */
export interface LinkingSyncState {
  readonly active: boolean;
  readonly done: number;
  readonly total: number;
  /** Messages replayed so far — fuels the indeterminate fallback label. */
  readonly messages: number;
}

interface ChatSyncState {
  /** Conversation ids (hex) recorded inside the current window. The
   *  banner shows `recentJoinIds.size`. Cleared by `prune()` once the
   *  window elapses with no new arrivals. */
  readonly recentJoinIds: ReadonlySet<string>;
  /** Wall-clock of the most recent `noteJoin` call. Drives the prune
   *  interval — see `useChatSyncBanner`. */
  readonly lastJoinAt: number;

  /** Linking history-sync progress (true-% banner). */
  readonly linkingSync: LinkingSyncState;

  noteJoin(conversationIdHex: string): void;
  /** Called by the banner hook on its expiry timer. Drops every id
   *  noted before `before`. */
  prune(before: number): void;

  /** Mark the linking history drain as started (banner becomes visible). */
  startLinkingSync(): void;
  /** Learn the % denominator from a transfer's declared `batch_total` WITHOUT
   *  advancing `done`. Called as soon as a transfer is seen (before its bundle
   *  is opened) so the banner can show a real percentage even if some bundles
   *  fail to decrypt/open. No-op once a total is already known or when absent. */
  noteLinkingBundleTotal(batchTotal: number | undefined): void;
  /** Record a replayed bundle: bumps `done` and learns `total` from the first
   *  non-undefined `batchTotal` seen. `messageDelta` adds to the indeterminate
   *  fallback's message count. */
  noteLinkingBundleReplayed(batchTotal: number | undefined, messageDelta: number): void;
  /** Mark the drain finished — clears `active` so the banner dismisses. */
  finishLinkingSync(): void;

  /** Test affordance — wipes the whole set immediately. */
  reset(): void;
}

const IDLE_LINKING_SYNC: LinkingSyncState = {
  active: false,
  done: 0,
  total: 0,
  messages: 0,
};

export const useChatSyncStore = create<ChatSyncState>((set) => ({
  recentJoinIds: new Set<string>(),
  lastJoinAt: 0,
  linkingSync: IDLE_LINKING_SYNC,
  noteJoin: (conversationIdHex) => {
    set((prev) => {
      const next = new Set(prev.recentJoinIds);
      next.add(conversationIdHex.toLowerCase());
      return { recentJoinIds: next, lastJoinAt: Date.now() };
    });
  },
  prune: (before) => {
    set((prev) => {
      if (prev.lastJoinAt > before) {
        // A newer join landed after the timer was scheduled — leave it.
        return prev;
      }
      if (prev.recentJoinIds.size === 0) {
        return prev;
      }
      return { recentJoinIds: new Set<string>(), lastJoinAt: prev.lastJoinAt };
    });
  },
  startLinkingSync: () => {
    set((prev) => {
      if (prev.linkingSync.active) return prev;
      return { linkingSync: { active: true, done: 0, total: 0, messages: 0 } };
    });
  },
  noteLinkingBundleTotal: (batchTotal) => {
    if (!batchTotal || batchTotal <= 0) return;
    set((prev) => {
      const ls = prev.linkingSync;
      if (ls.total > 0) return prev;
      return { linkingSync: { ...ls, active: true, total: batchTotal } };
    });
  },
  noteLinkingBundleReplayed: (batchTotal, messageDelta) => {
    set((prev) => {
      const ls = prev.linkingSync;
      // Learn the denominator from the FIRST transfer that carried it.
      const total = ls.total > 0 ? ls.total : batchTotal && batchTotal > 0 ? batchTotal : 0;
      return {
        linkingSync: {
          active: true,
          done: ls.done + 1,
          total,
          messages: ls.messages + Math.max(0, messageDelta),
        },
      };
    });
  },
  finishLinkingSync: () => {
    set((prev) => {
      if (!prev.linkingSync.active) return prev;
      return { linkingSync: IDLE_LINKING_SYNC };
    });
  },
  reset: () =>
    set({
      recentJoinIds: new Set<string>(),
      lastJoinAt: 0,
      linkingSync: IDLE_LINKING_SYNC,
    }),
}));
